import { useEffect, useRef, useState } from "react";

/**
 * Returns a fixed page-size `limit` based on viewport width, not content height.
 *
 * - Full desktop / full-screen width (>= 1600px)  -> 8 rows per page
 * - Anything narrower (resized window, devtools open, smaller screens) -> 9 rows
 *
 * Debounced (200ms) so rapid resize events don't cause a burst of refetches.
 */
const useTableHeight = (
  tableRef,
  breakpoint = 1600,
  wideLimit = 8,
  narrowLimit = 9,
) => {
  const [limit, setLimit] = useState(
    typeof window !== "undefined" && window.innerWidth >= breakpoint
      ? wideLimit
      : narrowLimit,
  );
  const debounceTimer = useRef(null);

  useEffect(() => {
    const calculateLimit = () => {
      const width = window.innerWidth;
      const newLimit = width >= breakpoint ? wideLimit : narrowLimit;
      setLimit((prev) => (prev !== newLimit ? newLimit : prev));
    };

    const debouncedCalculate = () => {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(calculateLimit, 200);
    };

    // run once immediately so the first paint has the right value
    calculateLimit();

    window.addEventListener("resize", debouncedCalculate);

    return () => {
      clearTimeout(debounceTimer.current);
      window.removeEventListener("resize", debouncedCalculate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakpoint, wideLimit, narrowLimit]);

  return limit;
};

export default useTableHeight;
